import { fetchProctorToken } from '../api/proctoringApi.js';

const encoder = new TextEncoder();

function base64Url(bytesOrString) {
  const bytes = typeof bytesOrString === 'string' ? encoder.encode(bytesOrString) : bytesOrString;
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

async function hmacSha256(message, secret) {
  const key = await globalThis.crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = await globalThis.crypto.subtle.sign('HMAC', key, encoder.encode(message));
  return new Uint8Array(signature);
}

export function buildProctorJwtPayload({
  courseId,
  elementId,
  candidateName,
  candidateId,
  eventName,
  organisationId,
  durationMinutes = 60,
  now = Math.floor(Date.now() / 1000),
}) {
  return {
    organisationId,
    sourceEventId: eventName,
    eventName,
    candidateName,
    candidateId: candidateId ?? `${candidateName}-${courseId}-${elementId}`,
    examDurationInMins: durationMinutes,
    iat: now,
    exp: now + 3600,
  };
}

export async function signHs256Jwt(payload, secret) {
  if (!secret) throw new Error('Remote proctoring token is not configured.');
  if (!globalThis.crypto?.subtle) throw new Error('Secure crypto is not available in this browser.');

  const encodedHeader = base64Url(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const encodedPayload = base64Url(JSON.stringify(payload));
  const signingInput = `${encodedHeader}.${encodedPayload}`;
  const signature = await hmacSha256(signingInput, secret);
  return `${signingInput}.${base64Url(signature)}`;
}

export async function getRemoteProctorToken(params) {
  const backendToken = await fetchProctorToken(params);
  const token = backendToken?.data?.token ?? backendToken?.token ?? null;
  if (token) return token;

  const secret = import.meta.env.VITE_RP_JWT_SECRET;
  const organisationId = import.meta.env.VITE_RP_ORG_ID;
  const payload = buildProctorJwtPayload({ ...params, organisationId });
  return signHs256Jwt(payload, secret);
}

export function hasRemoteProctorTokenSource() {
  return !!(import.meta.env.VITE_RP_TOKEN_ENDPOINT || import.meta.env.VITE_RP_JWT_SECRET);
}

export function shouldBypassProctoringForLocalDev() {
  return import.meta.env.VITE_ENV === 'development' && !hasRemoteProctorTokenSource();
}
